import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link, useParams } from 'react-router-dom';
import { categoryCards, featuredArticles, hotArticles } from '@/mocks/homeData';
import { useEffect } from 'react';

export default function CategoryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categoryCards.find(c => c.id === slug);

  const allArticles = [...featuredArticles, ...hotArticles];
  const categoryArticles = allArticles.filter(a => (a as any).categorySlug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (!category) return;

    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': `${category.name} - 吃瓜网`,
      'description': `${category.description} - 吃瓜网提供${category.count.toLocaleString()}条${category.name}相关爆料内容`,
      'url': `https://www.chiguawang.com/category/${category.id}`,
      'isPartOf': { '@type': 'WebSite', 'name': '吃瓜网', 'url': 'https://www.chiguawang.com' },
      'about': { '@type': 'Thing', 'name': category.name },
      'inLanguage': 'zh-CN',
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.chiguawang.com' },
        { '@type': 'ListItem', 'position': 2, 'name': '全部分类', 'item': 'https://www.chiguawang.com/category' },
        { '@type': 'ListItem', 'position': 3, 'name': category.name },
      ],
    };

    const schemas = [
      { id: 'collection-schema', data: collectionSchema },
      { id: 'breadcrumb-schema', data: breadcrumbSchema },
    ];

    schemas.forEach(({ id: sid, data }) => {
      const existing = document.getElementById(sid);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = sid;
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach(({ id: sid }) => {
        const el = document.getElementById(sid);
        if (el) el.remove();
      });
    };
  }, [category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-6">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-700 font-heading text-lg mb-2">分类未找到</p>
            <p className="text-foreground-400 text-sm mb-6">该分类可能不存在或已被移除</p>
            <Link
              to="/category"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-apps-line"></i> 查看全部分类
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18 bg-background-50">
        {/* Header */}
        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-5 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category" className="hover:text-primary-500 transition-colors whitespace-nowrap">全部分类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{category.name}</span>
            </nav>

            {/* Category Info */}
            <div className="flex items-center gap-4 md:gap-5">
              <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl ${category.lightColor} flex items-center justify-center flex-shrink-0`}>
                <i className={`${category.icon} text-2xl md:text-3xl ${category.textColor}`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{category.name}</h1>
                <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
              </div>
              <div className="ml-auto hidden sm:block">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-secondary-200/60 text-foreground-700 text-sm">
                  <i className="ri-file-list-3-line text-primary-500"></i>
                  <strong>{category.count.toLocaleString()}</strong> 条爆料
                </span>
              </div>
            </div>

            {/* Mobile count */}
            <div className="mt-4 sm:hidden">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50 border border-secondary-200/60 text-foreground-600 text-xs">
                <i className="ri-file-list-3-line text-primary-500"></i>
                共 <strong>{category.count.toLocaleString()}</strong> 条爆料
              </span>
            </div>
          </div>
        </section>

        {/* Sub-categories / Quick Filters */}
        <section className="py-4 md:py-5 px-4 md:px-6 bg-background-100 border-b border-secondary-200/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <span className="text-xs text-foreground-400 whitespace-nowrap flex-shrink-0">快速切换：</span>
              {categoryCards.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 flex-shrink-0 cursor-pointer ${
                    cat.id === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 border border-secondary-200/60 hover:border-primary-300 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} mr-1 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-foreground-600 text-sm">
                    共 <strong className="text-foreground-900">{categoryArticles.length}</strong> 篇相关内容
                  </p>
                  <div className="flex items-center gap-3">
                    <button className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors flex items-center gap-1 cursor-pointer">
                      <i className="ri-time-line"></i> 最新
                    </button>
                    <button className="text-xs text-foreground-400 hover:text-foreground-700 transition-colors flex items-center gap-1 cursor-pointer">
                      <i className="ri-fire-line"></i> 最热
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-2xl border border-secondary-200/60 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                    >
                      <div className="relative h-44 md:h-48 overflow-hidden">
                        <img
                          src={article.imageUrl || ''}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {(article as any).tag && (
                          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-semibold">
                            {(article as any).tag}
                          </span>
                        )}
                      </div>
                      <div className="p-4 md:p-5 flex-1 flex flex-col">
                        <h3 className="font-heading text-base md:text-lg text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2 flex-1">
                          {article.title}
                        </h3>
                        {(article as any).excerpt && (
                          <p className="text-foreground-500 text-sm line-clamp-2 mb-3">
                            {(article as any).excerpt}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-foreground-400 text-xs pt-3 border-t border-secondary-200/50">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(0)}万
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line"></i> {article.comments.toLocaleString()}
                          </span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16 md:py-20">
                <div className="w-20 h-20 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-6">
                  <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-600 font-heading text-lg mb-2">暂无内容</p>
                <p className="text-foreground-400 text-sm mb-6">该分类下暂时还没有爆料内容，我们会尽快更新</p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    to="/category"
                    className="whitespace-nowrap inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-secondary-100 text-foreground-700 text-sm font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-apps-line"></i> 全部分类
                  </Link>
                  <Link
                    to="/"
                    className="whitespace-nowrap inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-line"></i> 返回首页
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Bottom Nav */}
        <div className="px-4 md:px-6 pb-10 md:pb-14">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <Link
              to="/category"
              className="flex items-center gap-2 text-foreground-500 text-sm hover:text-primary-500 transition-colors py-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 全部分类
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-500 text-sm hover:text-primary-500 transition-colors py-2 cursor-pointer"
            >
              返回首页 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}